import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { providerService } from '@/services/providers';
import { UpdateProviderInput } from '@/features/providers/types';
import { toast } from 'sonner';
import { useCurrencyStore } from '@/stores/currencyStore';
import { CurrencyCode } from '@/utils/currencyFormatter';

export function useProviders() {
    const setProviderCurrency = useCurrencyStore(state => state.setProviderCurrency);

    return useQuery({
        queryKey: ['providers'],
        queryFn: async () => {
            const providers = await providerService.getProviders();

            // Cache currency for each provider from database
            providers.forEach(provider => {
                if (provider.currency) {
                    setProviderCurrency(provider.id, provider.currency as CurrencyCode);
                }
            });

            return providers;
        }
    });
}

export function useProvider(id: number) {
    const setProviderCurrency = useCurrencyStore(state => state.setProviderCurrency);

    return useQuery({
        queryKey: ['providers', id],
        queryFn: async () => {
            const provider = await providerService.getProviderById(id);

            // Cache currency for this provider from database
            if (provider && provider.currency) {
                setProviderCurrency(provider.id, provider.currency as CurrencyCode);
            }

            return provider;
        },
        enabled: !!id
    });
}

export function useProviderTypes() {
    return useQuery({
        queryKey: ['providerTypes'],
        queryFn: providerService.getProviderTypes
    });
}

export function useCreateProvider() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: providerService.createProvider,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['providers'] });
            toast.success('Provider created successfully');
        },
        onError: (error: Error) => {
            toast.error(error.message);
        }
    });
}

export function useUpdateProvider() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdateProviderInput }) =>
            providerService.updateProvider(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['providers'] });
            toast.success('Provider updated successfully');
        },
        onError: (error: Error) => {
            toast.error(error.message);
        }
    });
}

export function useDeleteProvider() {
    const queryClient = useQueryClient();
    
    return useMutation({
        mutationFn: providerService.deleteProvider,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['providers'] });
            toast.success('Provider deleted successfully');
        },
        onError: (error: Error) => {
            toast.error(error.message);
        }
    });
}
